import AgentFilter from "./agent-filter";
import AgentInteractions from "./agent-interactions";
import AgentInteractionsVolumeChart from "./agent-interactions-volume-chart";
import AgentMetrics from "./agent-metrics";
import ExploreFilters from "components-for/filters/explore-filters";
import MetricsOverTimeCharts from "components-for/metrics/metrics-over-time-charts";
import { Page, PageContent, PageHeader } from "@edgetier/client-components";
import { clearUrlParameters } from "@edgetier/utilities";
import useAgents from "hooks-for/agents/use-agents";
import useTagGroups from "hooks-for/tag-groups/use-tag-groups";
import useTags from "hooks-for/tags/use-tags";
import { Fragment, FunctionComponent, memo, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useInteractionMetricsAvailable } from "hooks-for/metrics/use-interaction-metrics-available";
import {
    IAgent,
    IInteractionMetricsAvailable,
    InteractionMetric,
    InteractionType,
    ITag,
    ITagGroup,
} from "@edgetier/watchtower-types";
import { EXPERIENCE_SCORE_METRIC_ENABLED } from "constants/feature-flags";
import { INTERACTIONS_TABLE_PREFIX } from "constants/interactions";
import { getDefaultFilters, isReviewAgentInUrl } from "./agent.utilities";
import { IProps } from "./agent.types";
import { SpinnerUntil } from "@edgetier/components";
import { Tabs, TabsButton, TabsButtons, TabsItem, TabsItems } from "@edgetier/tabs";
import { getInteractionTypeFromUrl } from "utilities-for/agents/get-interaction-type-from-url";
import AgentWritingIssuesTable from "pages/agent/agent-writing-issues-table";
import "./agent.scss";

/**
 * Agent review screen.
 * @returns Agent filters, charts, and metrics.
 */
const Agent: FunctionComponent<IProps> = () => {
    const { search } = useLocation();
    const { data: tagGroups, isError: isTagGroupsError, isLoading: areTagGroupsLoading } = useTagGroups();
    const { data: tags, isError: isTagsError, isLoading: areTagsLoading } = useTags();
    const { data: agents, isError: isAgentsError, isLoading: areAgentsLoading } = useAgents();

    const selectedInteractionType = useMemo(() => getInteractionTypeFromUrl(search), [search]);
    const {
        data: interactionMetricsAvailable,
        isError: isInteractionMetricsAvailableError,
        isLoading: areInteractionMetricsAvailableLoading,
    } = useInteractionMetricsAvailable({ interactionType: selectedInteractionType });

    // Default opening and closing date range for the filter.
    const defaultFilters = useMemo(() => getDefaultFilters(search), [search]);

    // Most of the page won't load until the user selects an agent.
    const isReviewAgentSelected = useMemo(() => isReviewAgentInUrl(search), [search]);

    const interactionMetricExcludeOptions = [
        InteractionMetric.QueueTime,
        ...(EXPERIENCE_SCORE_METRIC_ENABLED ? [] : [InteractionMetric.ExperienceScore]),
    ];

    return (
        <Page className="agent">
            <PageHeader title="Agent" />
            <PageContent>
                <SpinnerUntil<{
                    agents: IAgent[];
                    tags: ITag[];
                    tagGroups: ITagGroup[];
                    interactionMetricsAvailable: IInteractionMetricsAvailable;
                }>
                    data={{ agents, tagGroups, tags, interactionMetricsAvailable }}
                    isReady={
                        !areAgentsLoading &&
                        !areTagGroupsLoading &&
                        !areTagsLoading &&
                        !areInteractionMetricsAvailableLoading
                    }
                >
                    {(data) =>
                        isAgentsError || isTagsError || isTagGroupsError || isInteractionMetricsAvailableError ? (
                            "Error"
                        ) : data.agents.length === 0 ? (
                            "No agents available"
                        ) : (
                            <Fragment>
                                <div className="agent__select-agent">
                                    <AgentFilter defaultFilters={defaultFilters} />
                                </div>

                                {!isReviewAgentSelected && (
                                    <p className="agent__no-agent">Please select an agent to review</p>
                                )}

                                {isReviewAgentSelected && (
                                    <>
                                        <ExploreFilters
                                            defaultFilterValues={defaultFilters}
                                            hideFields={["agentId"]}
                                            customFieldItems={{
                                                interactionType: [InteractionType.Chat, InteractionType.Email],
                                            }}
                                            visibleFields={["search"]}
                                            tags={data.tags || []}
                                            tagGroups={data.tagGroups || []}
                                            submitOnChange={true}
                                            onChangeCallback={(parameters) =>
                                                clearUrlParameters(parameters, INTERACTIONS_TABLE_PREFIX)
                                            }
                                            getWarnings={(formValues) => {
                                                return {
                                                    ...(formValues.interactionType?.includes(InteractionType.Email) && {
                                                        [InteractionType.Email]:
                                                            "Email agent review is currently in beta",
                                                    }),
                                                };
                                            }}
                                        />

                                        <AgentMetrics
                                            defaultEndDateTime={defaultFilters.endDateTime}
                                            defaultStartDateTime={defaultFilters.startDateTime}
                                            interactionMetricsAvailable={data.interactionMetricsAvailable}
                                        />
                                        <AgentInteractionsVolumeChart
                                            defaultEndDateTime={defaultFilters.endDateTime}
                                            defaultStartDateTime={defaultFilters.startDateTime}
                                        />

                                        <Tabs activeTabId="performance">
                                            <TabsButtons>
                                                <TabsButton tabId="performance">Performance</TabsButton>
                                                <TabsButton tabId="spelling-and-grammar">
                                                    Spelling &#38; Grammar
                                                </TabsButton>
                                            </TabsButtons>
                                            <TabsItems>
                                                <TabsItem tabId="performance">
                                                    <MetricsOverTimeCharts
                                                        defaultEndDateTime={defaultFilters.endDateTime}
                                                        defaultStartDateTime={defaultFilters.startDateTime}
                                                        excludedInteractionMetrics={interactionMetricExcludeOptions}
                                                    />

                                                    <AgentInteractions
                                                        defaultEndDateTime={defaultFilters.endDateTime}
                                                        defaultStartDateTime={defaultFilters.startDateTime}
                                                    />
                                                </TabsItem>
                                                <TabsItem tabId="spelling-and-grammar">
                                                    <AgentWritingIssuesTable />
                                                </TabsItem>
                                            </TabsItems>
                                        </Tabs>
                                    </>
                                )}
                            </Fragment>
                        )
                    }
                </SpinnerUntil>
            </PageContent>
        </Page>
    );
};

export default memo(Agent);
